import * as React from 'react';
import { RatesRule, RatesColumn, Rates, getRate } from 'app/pages/AgentPricing/models';
import { isDefined } from 'app/helpers/objectHelper';


interface RatesTableRowProps {
    readonly?: boolean,
    rule: RatesRule, 
    columns: RatesColumn[], 
    rates: Rates, 
    selected: boolean,
    highlightColumnId?: string
    onChangeRate: (columnId: string, rowId: string, rate: string) => void
}

export default function RatesTableRow(props: RatesTableRowProps) {
    return props.readonly 
        ? ReadonlyRatesTableRow(props)
        : EditableRatesTableRow(props);
}

function ReadonlyRatesTableRow(props: RatesTableRowProps) {
    return  <tr className={props.selected ? "selected" : undefined}>
        <td>{props.rule.name}</td>
        {
            props.columns.map(y => {
                    const rate = getRate(props.rates, y.id, props.rule.code); 
                    return y.id === props.highlightColumnId
                        ? <td className="highlight" key={y.id + '|' + props.rule.code}>{rate}</td>
                        : <td key={y.id + '|' + props.rule.code}>{rate}</td>
            })
        }     
    </tr>
}

function EditableRatesTableRow(props: RatesTableRowProps) {
    return  <tr>
        <td>{props.rule.name}</td>
        {
            props.columns.map(y => {
                    const rate = getRate(props.rates, y.id, props.rule.code); 
                    return <td className="input-cell" key={y.id + '|' + props.rule.code}>
                        <input 
                            type="text" 
                            value={rate} 
                            onFocus={selectAllText} 
                            onKeyDown={handleArrowKey}
                            onChange={changeRate.bind(window, props, y.id)} 
                            onBlur={normalizeRate.bind(window, props, y.id)} />
                    </td>
            })
        }     
    </tr>
}

function changeRate(props: RatesTableRowProps, columnId: string, evt: React.ChangeEvent<HTMLInputElement>) {
    const value = evt.currentTarget.value;
    if (!/^[0-9]*(\.[0-9]{0,6})?$/.exec(value)) {
        evt.preventDefault();
        return;
    }

    if (parseFloat(value) > 100) {
        evt.preventDefault();
        return;
    }

    props.onChangeRate(
        columnId,
        props.rule.code,
        evt.currentTarget.value);
}

function normalizeRate(props: RatesTableRowProps, columnId: string, evt: React.SyntheticEvent<HTMLInputElement>) {
    const value = evt.currentTarget.value;
    if (value === '')
        return;

    let parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) {
        parsedValue = 0;
    }

    const formattedValue = parsedValue.toString();
    if (formattedValue !== value) {
        props.onChangeRate(
            columnId,
            props.rule.code,
            formattedValue);
    }
}

function selectAllText(evt: React.SyntheticEvent<HTMLInputElement>) {
    evt.currentTarget.setSelectionRange(0, evt.currentTarget.value.length);
}

function handleArrowKey(evt: React.KeyboardEvent<HTMLInputElement>) {
    if (evt.isDefaultPrevented())
        return;
    const cell = evt.currentTarget.parentElement as HTMLTableCellElement;
    if (!isDefined(cell.cellIndex))
        return;

    switch(evt.key) {
        case 'ArrowLeft':
            const previousCell = cell.previousElementSibling;
            focusCell(evt, previousCell);
            break;
        case 'ArrowRight':
            const nextCell = cell.nextElementSibling;
            focusCell(evt, nextCell);
            break;
        case 'ArrowUp':
        {
            const cellIndex = cell.cellIndex;
            const row = cell.parentElement;
            if (row == null)
                return;
            const previousRow = row.previousElementSibling;
            if (previousRow == null || !(previousRow as HTMLTableRowElement).cells)
                return;

            const upCell = (previousRow as HTMLTableRowElement).cells[cellIndex];
            focusCell(evt, upCell);
            break;
        }
        case 'ArrowDown':
        {
            const cellIndex = cell.cellIndex;
            const row = cell.parentElement;
            if (row == null)
                return;
            const nextRow = row.nextElementSibling;
            if (nextRow == null || !(nextRow as HTMLTableRowElement).cells)
                return;

            const upCell = (nextRow as HTMLTableRowElement).cells[cellIndex];
            focusCell(evt, upCell);
            break;
        }

        default:
            return;
    }
}


function focusCell(evt: React.SyntheticEvent<HTMLInputElement>, cell: Element|undefined|null) {
    if (!cell)
        return;

    let item = cell.firstElementChild;
    while (item !== null && item.nodeName.toUpperCase() !== 'INPUT') {
        item = item.nextElementSibling;
    }

    if (item) {
        const input = item as HTMLInputElement;
        if (!input.focus || !input.setSelectionRange)
            return;

        evt.preventDefault();
        input.setSelectionRange(0, input.value.length);
        input.focus();
    }
}